import {
  Grid,
  List,
  ListItemText,
  ListItemButton,
  Alert,
  ListItemIcon,
  Box,
  Typography,
  FormControl
} from '@mui/material';
import { useQuery } from 'react-query';
import { FC, Fragment, useState } from 'react';
import { PandaDocEditor } from '../../../components/pdf-viewer/PandadocEditor';
import {
  getDynamicDocumentationFileEditTemplateTokens,
  getDynamicDocumentationFileTemplate,
  getEditDynamicDocumentationFileTemplateSession,
  getDynamicDocumentationFileTemplatesFinancingTypes,
  updateDynamicDocumentationFileTemplate
} from '../../../fetch';
import { useSnackbar } from 'notistack';
import { copyTextToClipboard } from '../../../helpers';
import {
  IDropdownResponse,
  IDynamicFileMangementTemplateDetail,
  IDynamicFileMangementTemplateEditSession
} from '../../../models';
import { AutoCompleteMultiple, Button, ISelectOption, Loader } from '../../../components';
import { CopyAll } from '@mui/icons-material';

interface IDynamicFileMangementEditor {
  pandaDocTemplateId: string;
}

export const DynamicFileMangementEditor: FC<IDynamicFileMangementEditor> = ({
  pandaDocTemplateId
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [selectedDocumentTypes, setSelectedDocumentTypes] = useState<ISelectOption[]>([]);
  const [isUpdating, setUpdating] = useState(false);

  const { data: replacementFields, isLoading: isLoadingFields } = useQuery<
    IDropdownResponse[],
    Error
  >(['getDynamicDocumentationFileEditTemplateTokens-dynamic-management'], () =>
    getDynamicDocumentationFileEditTemplateTokens()
  );

  const { data, isLoading: isLoadingSession } = useQuery<
    IDynamicFileMangementTemplateEditSession,
    Error
  >(['getEditSalesToolsContractTemplateSession-dynamic-management', pandaDocTemplateId], () =>
    getEditDynamicDocumentationFileTemplateSession(pandaDocTemplateId)
  );

  const { isLoading: isLoadingFinancingTypes, data: financingTypes } = useQuery<
    IDropdownResponse[],
    Error
  >(
    ['getDynamicDocumentationFileTemplatesFinancingTypes'],
    async () => await getDynamicDocumentationFileTemplatesFinancingTypes()
  );

  const { data: currentTemplate, isLoading: isLoadingCurrentFile } = useQuery<
    IDynamicFileMangementTemplateDetail,
    Error
  >(
    ['getDynamicDocumentationFileTemplate-dynamic-management', pandaDocTemplateId],
    () => getDynamicDocumentationFileTemplate(pandaDocTemplateId),
    {
      onSuccess: d => {
        // New 'All' option comes through as a number, so ensure all values are strings
        const stringFinancingTypes = d.financingTypes.map(value => value.toString());
        const selected: ISelectOption[] = [];
        financingTypes.forEach(type => {
          if (stringFinancingTypes.includes(type.value)) {
            selected.push({
              value: type.value,
              label: type.description
            });
          }
        });
        setSelectedDocumentTypes(selected);
      },
      enabled: !!financingTypes
    }
  );

  const validReplacementFields = replacementFields?.filter(field => field !== null);

  if (isLoadingCurrentFile || isLoadingFields || isLoadingSession) {
    return <Loader type="inline" position="centered" />;
  }

  if (currentTemplate?.template?.isDeletedInPandaDoc) {
    return (
      <Alert severity="error">
        File has been deleted in Pandadoc. Go back to select a different file.
      </Alert>
    );
  }
  if (!!currentTemplate) {
    return (
      <Box position="relative">
        <Box display="flex" alignItems="center" mb={1} gap={0.75}>
          <FormControl fullWidth variant="outlined" size="small" required>
            <AutoCompleteMultiple
              name="financingType"
              limitTags={-1}
              options={
                financingTypes
                  ? financingTypes
                      // remove selected financing types from the list upon selection
                      .filter(type => !selectedDocumentTypes.map(t => t.value).includes(type.value))
                      .map(type => ({
                        value: type.value,
                        label: type.description
                      }))
                  : []
              }
              label={isLoadingFinancingTypes ? 'Loading...' : 'Financing Type(s)'}
              value={selectedDocumentTypes}
              required
              onChange={(event, newValue) => {
                if (!newValue || (Array.isArray(newValue) && newValue.length === 0)) {
                  setSelectedDocumentTypes([]);
                } else if (Array.isArray(newValue)) {
                  setSelectedDocumentTypes(newValue.map(option => option));
                }
              }}
            />
          </FormControl>
          <Button
            id="save-financing-types"
            disabled={selectedDocumentTypes?.length === 0 || isUpdating}
            variant="contained"
            color="primary"
            sx={{ height: 40 }}
            onClick={async () => {
              try {
                setUpdating(true);
                await updateDynamicDocumentationFileTemplate(pandaDocTemplateId, {
                  financingTypes: selectedDocumentTypes.map(type => type.value),
                  isActive: currentTemplate.template.isActive
                });
                enqueueSnackbar('Financing types updated', { variant: 'success' });
              } catch (error) {
                enqueueSnackbar('Failed to update financing types, please try again', {
                  variant: 'error'
                });
              } finally {
                setUpdating(false);
              }
            }}
          >
            Save
          </Button>
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12} lg={3}>
            {isLoadingFields && <Loader type="overlay" position="centered" />}
            <Alert severity="info" sx={{ marginBottom: 2 }}>
              Click on the "Plus" (+) sign in the top right corner. From the Fillable Fields
              section, add checkmarks where needed, and add text fields where filled text should
              appear. For filled text, choose the "Properties" icon. Then, copy and paste the field
              needed from the menu below, to the "Merge Field".
            </Alert>
            {!isLoadingFields && validReplacementFields?.length === 0 ? (
              <Typography variant="body2" textAlign="center" sx={{ mt: 2 }}>
                No replacement fields available
              </Typography>
            ) : (
              <List component="div" dense sx={{ maxHeight: 700, overflowY: 'auto' }}>
                {validReplacementFields?.map((field, index) => (
                  <Fragment key={index}>
                    <ListItemButton
                      dense
                      onClick={async () => {
                        try {
                          await copyTextToClipboard(field?.value);
                          enqueueSnackbar('Field copied to clipboard', { variant: 'success' });
                        } catch (error) {
                          enqueueSnackbar('Failed to copy to clipboard, please try again', {
                            variant: 'error'
                          });
                        }
                      }}
                    >
                      <ListItemText primary={field?.description} />
                      <ListItemIcon>
                        <CopyAll />
                      </ListItemIcon>
                    </ListItemButton>
                  </Fragment>
                ))}
              </List>
            )}
          </Grid>
          <Grid item xs={12} lg={9}>
            {isLoadingSession && <Loader type="overlay" position="centered" />}
            {!isLoadingSession && (
              <PandaDocEditor
                pandaDocTemplateId={pandaDocTemplateId}
                token={data.key}
                editingSessionId={data.editingSessionId}
              />
            )}
          </Grid>
        </Grid>
      </Box>
    );
  }
  return null;
};
